import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, CheckCircle2, ArrowRight, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const PROVINCES = ["AB","BC","SK","MB","ON","QC","NB","NS","PE","NL","YT","NT","NU"];

export default function VendorApply() {
  const [taxo, setTaxo] = useState({ categories: [], provinces: [], plans: [] });
  const [form, setForm] = useState({
    name: "", category: "", provinces: [], cities: "",
    phone: "", email: "", website: "", description: "",
    rate: "", years_in_business: "", insured: false, wcb_number: "",
    logo_url: "", plan: "free",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");
  const captcha = useCaptcha();

  useEffect(() => {
    fetch(`${API}/api/vendors/categories`).then((r) => r.json()).then(setTaxo).catch(() => {});
  }, []);

  // Handle Stripe return — /vendors/apply?paid=1&app_id=…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1" && params.get("app_id")) {
      const sessionCheckId = params.get("session_id");
      const appId = params.get("app_id");
      (async () => {
        try {
          // Try the status endpoint — walks Stripe to confirm payment.
          // If we don't have a session_id we fall back to the vendor's
          // stored checkout_session_id server-side.
          if (sessionCheckId) {
            await fetch(`${API}/api/vendors/apply/checkout/status/${sessionCheckId}`);
          }
          setSuccess({ application_id: appId, paid: true });
          toast.success("Payment received — expedited review starts now.");
        } catch (_) { /* noop */ }
      })();
    } else if (params.get("cancelled") === "1" && params.get("app_id")) {
      setSuccess({ application_id: params.get("app_id") });
      toast.info("Checkout cancelled — your free application is still in the queue.");
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProv = (p) => set("provinces", form.provinces.includes(p) ? form.provinces.filter((x) => x !== p) : [...form.provinces, p]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.category || !form.description) {
      setErr("Please fill in name, category, and description (20+ characters)."); return;
    }
    if (form.description.trim().length < 20) {
      setErr("Description must be at least 20 characters so landlords know what you do."); return;
    }
    if (!(form.phone || form.email || form.website)) {
      setErr("Please provide at least one contact method (phone, email, or website)."); return;
    }
    if (form.provinces.length === 0) {
      setErr("Please pick at least one province you service."); return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        years_in_business: form.years_in_business ? parseInt(form.years_in_business, 10) : null,
      };
      const r = await fetch(`${API}/api/vendors/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...captcha.headers() },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        if (/security\s+check|captcha/i.test(d.detail || "")) captcha.refresh();
        throw new Error(d.detail || "Couldn't submit application");
      }
      setSuccess(d);
      toast.success("Application received — we'll be in touch.");
    } catch (e2) {
      setErr(e2.message || "Couldn't submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    // Pay-fee flow — creates a Stripe Checkout session for the CA$29
    // one-time expedited-review application fee, redirects the vendor
    // to Stripe, and lets the success URL confirm on return.
    const payFee = async () => {
      try {
        const r = await fetch(`${API}/api/vendors/apply/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            application_id: success.application_id,
            origin_url: window.location.origin,
          }),
        });
        const d = await r.json();
        if (!r.ok || !d.url) throw new Error(d.detail || "Could not create checkout");
        window.location.href = d.url;
      } catch (e2) {
        toast.error(e2.message || "Payment could not be started");
      }
    };
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="vendor-apply-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Application received</h1>
        <p className="text-base text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
          Thanks for applying to join the Central Rentals vendor roster. Our team reviews every application within 2 business days. You&apos;ll hear from us at <strong>{form.email || form.phone || "the contact you provided"}</strong>.
        </p>
        <div className="mt-2 text-xs text-slate-400 font-mono">Application ID: {success.application_id}</div>
        <div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 max-w-md mx-auto text-left">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Optional · fast-track</div>
          <div className="font-display text-lg font-bold text-slate-900 mt-1">Skip the queue — CA$29 one-time expedited review</div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Get reviewed within 4 business hours instead of 2 business days. Non-refundable if rejected on legitimate grounds. One-time — no recurring charge.
          </p>
          <button
            type="button"
            onClick={payFee}
            data-testid="vendor-apply-pay-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
          >
            Pay CA$29 · expedited review <ArrowRight className="w-4 h-4"/>
          </button>
          <div className="mt-2 text-[11px] text-slate-500 text-center">
            Powered by Stripe · card / Apple Pay / Google Pay · one-time charge
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-semibold text-sm">
            Back to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="vendor-apply-page">
      <Link to="/" data-testid="vendor-apply-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-4 h-4" /> Homepage
      </Link>
      <header className="mb-7">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-[0.18em] font-bold">
          <Wrench className="w-3 h-3" /> Vendor application
        </div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-3">Get listed on Central Rentals</h1>
        <p className="text-base text-slate-600 mt-3 max-w-2xl leading-relaxed">
          Canadian landlords on Central Rentals need plumbers, electricians, HVAC techs, locksmiths, contractors, accountants, and more. Apply once — our team reviews every application within 2 business days.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-5 bg-white rounded-2xl border border-slate-200 p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <Field label="Business name *" testid="va-name">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={120} className="va-input" data-testid="va-name-input" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Trade / category *" testid="va-category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} required className="va-input" data-testid="va-category-input">
              <option value="">Choose…</option>
              {(taxo.categories || []).map((c) => (
                <option key={c} value={c}>{c.replace(/-/g, " ")}</option>
              ))}
            </select>
          </Field>
          <Field label="Years in business" testid="va-years">
            <input type="number" min={0} max={150} value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} className="va-input" data-testid="va-years-input" />
          </Field>
        </div>

        <Field label="Provinces you service *" testid="va-provinces" hint="Pick all that apply">
          <div className="flex flex-wrap gap-2">
            {PROVINCES.map((p) => (
              <button type="button" key={p} onClick={() => toggleProv(p)} data-testid={`va-province-${p}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                  form.provinces.includes(p)
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}>
                {p}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Cities / service area" testid="va-cities" hint="e.g. Toronto, Mississauga, Hamilton">
          <input value={form.cities} onChange={(e) => set("cities", e.target.value)} className="va-input" data-testid="va-cities-input" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone" testid="va-phone">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="va-input" placeholder="(403) 555-1234" data-testid="va-phone-input" />
          </Field>
          <Field label="Email" testid="va-email">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="va-input" placeholder="hello@example.ca" data-testid="va-email-input" />
          </Field>
        </div>

        <Field label="Website" testid="va-website">
          <input value={form.website} onChange={(e) => set("website", e.target.value)} className="va-input" placeholder="https://" data-testid="va-website-input" />
        </Field>

        <Field label="Tell landlords what you do *" testid="va-desc" hint="20+ characters. Mention specialties, certifications, and what makes you reliable.">
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} required minLength={20} maxLength={2000} rows={5} className="va-input" data-testid="va-desc-input" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Typical rate" testid="va-rate" hint="e.g. $120/hr, flat fee, on quote">
            <input value={form.rate} onChange={(e) => set("rate", e.target.value)} className="va-input" data-testid="va-rate-input" />
          </Field>
          <Field label="WCB / WSIB number" testid="va-wcb" hint="If you carry it">
            <input value={form.wcb_number} onChange={(e) => set("wcb_number", e.target.value)} className="va-input" data-testid="va-wcb-input" />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.insured} onChange={(e) => set("insured", e.target.checked)} data-testid="va-insured" className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          We carry general liability insurance
        </label>

        <Field label="Logo or photo" testid="va-logo" hint="Optional — helps your card stand out.">
          <PhotoPicker
            value={form.logo_url}
            onChange={(url) => set("logo_url", url || "")}
            kind="listing"
            apiBase={`${API}/api`}
            testid="va-logo-picker"
            label="Upload logo"
          />
        </Field>

        {err && <div data-testid="va-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-3">{err}</div>}

        <MathCaptcha {...captcha} testId="va-captcha" />

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> We review within 2 business days. We&apos;ll email you once you&apos;re live.
          </div>
          <button type="submit" disabled={submitting} data-testid="va-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </div>
      </form>

      <style>{`.va-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; transition: border-color .15s; } .va-input:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }`}</style>
    </div>
  );
}

function Field({ label, hint, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
